import React, { useState } from "react";
import { Select, SelectProps, Spin } from "antd";
import { tv, type VariantProps } from "tailwind-variants";
import { CaretDown } from "@phosphor-icons/react";

const selectStyle = tv({
  base: "rounded-md !test-base",
  variants: {
    statusSelect: {
      default: "",
      disabled: "bg-[var(--bgBlackOpacity)] text-gray-400 cursor-not-allowed",
      loading: "animate-pulse",
    },
    bg: {
      default: "!text-black",
    },
    sizeSelect: {
      sm: "h-[32px]",
      md: "h-[30px] xl:h-[36px] 2xl:h-[40px] w-full",
      md2: "2xl:text-[14px] 2xl:h-[38px] xl:text-[11px] xl:h-[26px] lg:text-[9px] lg:h-[21px] 2xl:leading-[20px] xl:leading-[16px] lg:leading-[12px]",
      lg: "h-[48px]",
    },
  },
  defaultVariants: {
    bg: "default",
    statusSelect: "default",
    sizeSelect: "md",
  },
});

type SelectCommonProps = SelectProps &
  Partial<VariantProps<typeof selectStyle>> & {
    className?: string;
    defaultValue?: string | number;
    placeholderSelect?: string;
    widthSelect?: string;
    variant?: "filled" | "outlined" | "borderless";
    options?: { label: React.ReactNode; value: string | number }[];
    selectType?: "select" | "filterSelect";
  };

const SelectCommon: React.FC<SelectCommonProps> = ({
  statusSelect,
  sizeSelect,
  bg,
  loading,
  className = "",
  defaultValue = "",
  widthSelect,
  variant = "outlined",
  placeholderSelect = "Chọn",
  options = [],
  selectType = "select",
  disabled = false,
  ...props
}) => {
  const [inputValue, setInputValue] = useState<string | number>(defaultValue);
  const mergedClassName = `${selectStyle({
    statusSelect,
    sizeSelect,
    bg,
  })} ${className}`;
  const isFilterSelect = selectType === "filterSelect";

  const handleSearch = (value: string) => {
    setInputValue(value);
  };

  return (
    <Select
      placeholder={placeholderSelect}
      style={{ width: "100%", minWidth: widthSelect }}
      className={mergedClassName}
      variant={variant}
      options={options}
      showSearch={isFilterSelect}
      notFoundContent={loading ? <Spin size="small" /> : "Chưa có dữ liệu"}
      filterOption={
        isFilterSelect
          ? (input, option) =>
              (option?.label as string)
                ?.toLowerCase()
                .includes(input.toLowerCase())
          : undefined
      }
      disabled={disabled}
      loading={loading}
      suffixIcon={<CaretDown size={16} />}
      onSearch={isFilterSelect ? handleSearch : undefined}
      {...(inputValue !== "" ? { defaultValue: inputValue } : {})}
      {...props}
    />
  );
};

export default SelectCommon;
